import React, { useEffect, useState } from "react";
import { Select, Form, notification } from "antd";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";

/**
 * Available Locations Selector - fetches available locations for partners
 * @component
 * @alias AvailableLocationsSelect
 * @property {string} label - label for the form item
 * @property {string} name - form field name
 * @property {any} initialValue - initial value for select
 * @property {bool} readOnly - sets Form Item read-only
 * @property {bool} required - sets Form Item required - default false
 * @property {string} validationMessage - custom validation message
 * @property {string} size - size of the select component
 * @property {function} onChange - callback function when selection changes
 * @property {string} mode - select mode (single/multiple) - default "single"
 * @property {bool} disabled - disables the select component
 * @property {string} className - CSS class name
 * @property {bool} clearable - allows clearing the selection
 * @returns <Form.Item /> containing <Select />
 */
const AvailableLocationsSelect = ({ 
  label,
  name,
  initialValue, 
  required = false, 
  readOnly = false, 
  validationMessage,
  size = "default",
  onChange = null, 
  clearable = true,
  mode = "single",
  disabled = false,
  className = "default-item-class"
}) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedValues, setSelectedValues] = useState(initialValue || (mode === "multiple" ? [] : null));

  useEffect(() => {
    let isMounted = true;

    setLoading(true);
    coreApi
      .get("/partners/available-locations")
      .then((response) => {
        if (isMounted) {
          const locationObjects = response.data
            .filter(location => location && location.value)
            .map((location) => ({
              id: location.value, //e.g. 'city-26' или 'loc-9'
              name: location.type === 'location' && location.cityBranchName
                ? `${location.cityBranchName} - ${location.name}`
                : location.name,
              type: location.type,
              cityBranchName: location.cityBranchName,
              originalData: location
            }));

          const cities = locationObjects.filter(loc => loc.type === 'city');
          const locs = locationObjects.filter(loc => loc.type === 'location');
          
          const grouped = [];
          cities.forEach(city => {
            grouped.push(city);
            const cityLocs = locs.filter(loc => 
              loc.cityBranchName === city.name
            );
            grouped.push(...cityLocs);
          });

          const orphanLocs = locs.filter(loc => 
            !cities.some(city => city.name === loc.cityBranchName)
          );
          grouped.push(...orphanLocs);
          
          setLocations(grouped);
        }
      })
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message });
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const getCityLocations = (cityName) => {
    return locations
      .filter(loc => loc.type === 'location' && loc.cityBranchName === cityName)
      .map(loc => loc.id);
  };

  const handleLocationChange = (locationIds) => {
    if (mode === "multiple") {
      const previousValues = selectedValues || [];
      const newValues = locationIds || [];

      const added = newValues.filter(v => !previousValues.includes(v));
      const removed = previousValues.filter(v => !newValues.includes(v));

      let finalValues = [...newValues];

      added.forEach(value => {
        if (value.startsWith('city-')) {
          const city = locations.find(loc => loc.id === value);
          if (city) {
            const cityLocations = getCityLocations(city.name);
            cityLocations.forEach(locValue => {
              if (!finalValues.includes(locValue)) {
                finalValues.push(locValue);
              }
            });
          }
        }
      });

      removed.forEach(value => {
        if (value.startsWith('city-')) {
          const city = locations.find(loc => loc.id === value);
          if (city) {
            const cityLocations = getCityLocations(city.name);
            finalValues = finalValues.filter(v => !cityLocations.includes(v));
          }
        }
      });

      removed.forEach(value => {
        if (value.startsWith('loc-')) {
          const location = locations.find(loc => loc.id === value);
          if (location && location.cityBranchName) {
            const city = locations.find(loc => 
              loc.type === 'city' && loc.name === location.cityBranchName
            );
            if (city) {
              const cityLocations = getCityLocations(city.name);
              const hasOtherLocations = cityLocations.some(locValue => 
                finalValues.includes(locValue) && locValue !== value
              );
              if (!hasOtherLocations && finalValues.includes(city.id)) {
                finalValues = finalValues.filter(v => v !== city.id);
              }
            }
          }
        }
      });

      setSelectedValues(finalValues);

      if (form && name) {
        form.setFieldValue(name, finalValues);
      }

      if (onChange) {
        const selectedLocations = locations.filter(location => 
          finalValues.includes(location.id)
        );
        onChange(selectedLocations);
      }
    } else {
      setSelectedValues(locationIds);
      
      if (form && name) {
        form.setFieldValue(name, locationIds);
      }
      
      if (onChange) {
        const selectedLocation = locations.find(location => location.id === locationIds);
        onChange(selectedLocation);
      }
    }
  };

  const filterOption = (input, option) => {
    if (typeof option.children === "string") {
      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    return false;
  };

  const renderLocationOption = (location) => {
    if (location.type === 'location') {
      return (
        <Select.Option 
          key={`location-${location.id}`} 
          value={location.id}
          style={{ paddingLeft: '24px' }}
        >
          ↳ {location.originalData.name}
        </Select.Option>
      );
    }
    return (
      <Select.Option 
        key={`location-${location.id}`} 
        value={location.id}
      >
        {location.name}
      </Select.Option>
    );
  };

  return (
    <Form.Item
      className={className}
      hasFeedback
      name={name}
      label={label}
      initialValue={initialValue}
      valuePropName={"value"}
      rules={[
        {
          required: required,
          message: validationMessage || t("partners.validation.location"),
        },
      ]}
    >
      <Select
        size={size}
        allowClear={clearable}
        disabled={disabled || readOnly}
        bordered={!readOnly}
        loading={loading}
        showSearch={true}
        mode={mode}
        value={selectedValues}
        placeholder={t("partners.selectAvailableLocation")}
        filterOption={filterOption}
        onChange={handleLocationChange}
      >
        {locations.map((location) => renderLocationOption(location))}
      </Select>
    </Form.Item>
  );
};

export default AvailableLocationsSelect; 